import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  ChevronRight,
  FolderOpen,
  Hotel,
  Loader2,
  PlaneTakeoff,
  Plus,
  Search,
  Users,
  Utensils,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { usePnrSearch } from '@/lib/cases/pnr-groups';
import { useFlightHits } from '@/lib/flights/use-flights';
import type { IFlightMonitorRow } from '@/lib/flights/types';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { rebookingStatusLabel, REBOOKING_STATUS_TONE } from '@/routes/disruptions/labels';
import { disruptionEntry } from '@/routes/disruptions/disruption-entry';
import { CountChip } from '@/routes/disruptions/board-chrome';
import { FlightActionColumns, FlightRowItem } from '@/routes/flights/flight-row';
import { useClockBasis } from '@/routes/flights/clock-basis';
import { normalizeStatus } from '@/routes/flights/status';
import { declareFlightsState, declareSourceState } from '@/routes/declare/declare-route.state';
import { touchScreen } from '@/lib/touch-screen';
import { PASSENGER_SEARCH_INPUT_ID, formatShortDateTime } from './passenger-search-model';

// The UNIVERSAL search — ONE component, mounted on two surfaces:
//   - the operator's home («Resumen», /dashboard) — the operator's first question:
//     "where is this passenger / what's going on with this flight?";
//   - the /passengers route (the sidebar's "Passengers" entry, the same search
//     as a page of its own).
// Two tenant-wide lookups fan out in parallel on the same needle:
//   - PASSENGERS — BFF /v1/cases/passengers/search (cases third roster scope,
//     last 24h). The row is the "work on it" affordance (opens the FULL
//     PnrOperationCenter in place — zero navigation); a slim side action jumps
//     to the CASE itself (/cases/:urn), or into the declare flow on /express
//     when the hit somehow carries none.
//   - FLIGHTS — BFF /v1/flights?q= (the monitor read, ±24h), whose rows carry
//     the live case/disruption marks. Drawn as the /flights board draws them
//     (`FlightRowItem`, its status in the same place — PO 2026-11-01: «debe
//     mostrarse igual»), each with its ONE action on the right: «Operar disrupción»
//     (its disruption), «Abrir caso» (its case), «Declarar disrupción» (nothing
//     open: hands the flight to the declare confirm — one confirmation, scope choice
//     included), nothing for a flight that already arrived (the board's own rule).
//
// The results are the page while a search runs (PO 2026-11-01: «es mejor solo mostrar
// los resultados de la búsqueda»): they render as full sections under the input, and
// `onActiveChange` tells the host to put its own content away. `aside` sits beside the
// input (the «Resumen» puts its «Declarar disrupción» button there). Esc clears.

const SEARCH_DEBOUNCE_MS = 250;
/** The results are a page, not a dropdown — but still a jump list: cap the flights. */
const MAX_FLIGHT_HITS = 10;

export function PassengerSearch({
  onOpen,
  autoFocus = true,
  aside,
  onActiveChange,
}: {
  onOpen: (hit: DisruptionPnrGroupRow) => void;
  autoFocus?: boolean;
  /** Beside the input (the «Resumen» declare button). */
  aside?: ReactNode;
  /** A search is showing results (true) or the host's own content can show (false). */
  onActiveChange?: (active: boolean) => void;
}) {
  const { t } = useTranslation('cases');
  const [input, setInput] = useState('');
  const [needle, setNeedle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Land the operator straight in the input (jsx-a11y-friendly: focus once on
  // mount through the ref, not the autoFocus prop — same as the Express entry). Not
  // on a touch screen: the keyboard would cover «Resumen» and, on an iPad, take it out
  // of full screen (`lib/touch-screen.ts`).
  useEffect(() => {
    if (autoFocus && !touchScreen()) inputRef.current?.focus();
  }, [autoFocus]);

  // Debounce typing → the query needle (previous results linger via
  // placeholderData, so the list never flashes empty between keystrokes).
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(input), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [input]);

  const search = usePnrSearch(needle);
  const flightSearch = useFlightHits(needle);
  const hits = search.data?.items ?? [];
  const flightHits = (flightSearch.data?.items ?? []).slice(0, MAX_FLIGHT_HITS);
  const searching = needle.trim().length >= 2;
  const anyHit = hits.length > 0 || flightHits.length > 0;
  const loading = (search.isLoading || flightSearch.isLoading) && !anyHit;
  const failed = search.isError && flightSearch.isError;
  const [clock] = useClockBasis();

  useEffect(() => {
    onActiveChange?.(searching);
  }, [searching, onActiveChange]);

  // A flight hit navigates by what the flight is PART OF: its disruption first
  // (the operator's ask: flight → the disruption it belongs to), else its case.
  // A flight with NEITHER declares: the row hands the flight to the Express
  // confirm via router state — one confirmation (scope choice included) opens a
  // whole-flight disruption, instead of the old dead-end on the flights board.
  const openFlight = (row: IFlightMonitorRow) => {
    if (row.disruptionUrn) {
      // An existing disruption opens on its default view (`disruptionEntry`).
      const entry = disruptionEntry(row.disruptionUrn);
      navigate(entry.to, { state: entry.state });
    } else if (row.caseUrn) {
      navigate(`/cases/${encodeURIComponent(row.caseUrn)}`);
    } else {
      navigate('/express', { state: { candidate: row } });
    }
  };

  // The passenger row's side action: the CASE is the destination (the row itself
  // opens the operation center in place). A hit without a case — defensive; the
  // search only reads cases today — enters the declare flow (unified-declare
  // decision 1): with a flight, step 2 with that flight already chosen; with
  // none, the departures board (the loose-PNR source is withdrawn for now,
  // 2026-11-04). Router state, never query params.
  const openHitCase = (hit: DisruptionPnrGroupRow) => {
    if (hit.caseUrn) {
      navigate(`/cases/${encodeURIComponent(hit.caseUrn)}`);
    } else if (hit.flightUrn) {
      navigate('/express', { state: declareFlightsState([hit.flightUrn]) });
    } else {
      navigate('/express', { state: declareSourceState('BOARD') });
    }
  };

  return (
    <section aria-label={t('search.label')} className="flex flex-col gap-5">
      <div className="flex flex-wrap items-stretch gap-3">
        <div className="relative min-w-0 flex-[999_1_26rem]">
          <Search
            className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id={PASSENGER_SEARCH_INPUT_ID}
            ref={inputRef}
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter opens the FIRST rendered hit — flights lead, so a typed
              // flight code lands on the flight (→ its disruption).
              if (e.key === 'Enter') {
                if (flightHits.length > 0) openFlight(flightHits[0]!);
                else if (hits.length > 0) onOpen(hits[0]!);
              }
              if (e.key === 'Escape') setInput('');
            }}
            placeholder={t('search.placeholder')}
            aria-label={t('search.inputLabel')}
            data-testid="dashboard-search"
            className="h-16 w-full rounded-xl border border-border bg-background pl-14 pr-24 text-[17px] shadow-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:ring-4 focus:ring-primary/15 [&::-webkit-search-cancel-button]:hidden"
          />
          {input ? (
            <button
              type="button"
              onClick={() => {
                setInput('');
                inputRef.current?.focus();
              }}
              aria-label={t('search.clear')}
              title={t('search.clear')}
              data-testid="dashboard-search-clear"
              className="absolute right-3 top-1/2 inline-flex h-10 -translate-y-1/2 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Esc
            </button>
          ) : (
            <kbd className="pointer-events-none absolute right-5 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground sm:block">
              ⌘ K
            </kbd>
          )}
        </div>
        {aside}
      </div>

      {searching ? (
        <div className="flex flex-col gap-4" data-testid="dashboard-search-results">
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t('search.searching')}
            </div>
          ) : failed ? (
            <p className="py-4 text-sm text-destructive">{t('search.failed')}</p>
          ) : !anyHit ? (
            <p
              className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground"
              data-testid="dashboard-search-empty"
            >
              {t('search.empty')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground" data-testid="dashboard-search-summary">
                {t('search.resultsFor', { q: needle.trim() })}
              </p>
              {flightHits.length > 0 ? (
                <ResultsSection
                  title={t('search.group.flights')}
                  count={flightHits.length}
                  testId="dashboard-search-flights"
                  aside={
                    <Link to="/flights" className="text-sm font-medium text-primary hover:underline">
                      {t('search.openFlights')}
                    </Link>
                  }
                >
                  <div className="overflow-x-auto">
                    <div className="min-w-0 sm:min-w-[56rem]">
                      <FlightActionColumns clock={clock} />
                      <ul className="divide-y divide-border/60">
                        {flightHits.map((row) => (
                          <li key={row.flightUrn} data-testid="dashboard-search-flight-hit">
                            <FlightRowItem
                              row={row}
                              clock={clock}
                              action={<FlightHitAction row={row} onDeclare={() => openFlight(row)} />}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </ResultsSection>
              ) : null}
              {hits.length > 0 ? (
                <ResultsSection
                  title={t('search.group.passengers')}
                  count={hits.length}
                  testId="dashboard-search-passengers"
                >
                  <ul className="divide-y divide-border/60 border-t border-border">
                    {hits.map((hit) => (
                      <SearchHitRow
                        key={hit.subCaseUrn}
                        hit={hit}
                        onOpen={() => onOpen(hit)}
                        onOpenCase={() => openHitCase(hit)}
                      />
                    ))}
                  </ul>
                </ResultsSection>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

/** A results group — one bordered card, its name and count on top (the boards' shape). */
function ResultsSection({
  title,
  count,
  aside,
  children,
  testId,
}: {
  title: string;
  count: number;
  aside?: ReactNode;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section aria-label={title} className="overflow-hidden rounded-lg border border-border bg-card" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        <CountChip n={count} testId={`${testId}-count`} />
        {aside ? <div className="ml-auto">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * A flight hit's ONE action, by what the flight is part of: its disruption ▸ «Operar
 * disrupción» (its default view); its case ▸ «Abrir caso»; arrived ▸ none (the /flights
 * board's rule: nothing left to declare); otherwise ▸ «Declarar disrupción», filled —
 * the flight goes to the declare confirm (router state, never the URL).
 */
function FlightHitAction({ row, onDeclare }: { row: IFlightMonitorRow; onDeclare: () => void }) {
  const { t } = useTranslation('cases');
  if (row.disruptionUrn) {
    const entry = disruptionEntry(row.disruptionUrn);
    return (
      <Button asChild variant="outline" size="sm" className="h-9 gap-1 font-semibold">
        <Link to={entry.to} state={entry.state} data-testid="flight-hit-operate">
          {t('search.action.operate')}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </Button>
    );
  }
  if (row.caseUrn) {
    return (
      <Button asChild variant="outline" size="sm" className="h-9 gap-1 font-semibold">
        <Link to={`/cases/${encodeURIComponent(row.caseUrn)}`} data-testid="flight-hit-case">
          {t('search.action.openCase')}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </Button>
    );
  }
  if (normalizeStatus(row.status) === 'ARRIVED') {
    return (
      <span className="text-xs text-muted-foreground" data-testid="flight-hit-arrived">
        {t('network:row.disabled.arrived')}
      </span>
    );
  }
  return (
    <Button size="sm" className="h-9 gap-1.5 font-semibold" onClick={onDeclare} data-testid="flight-hit-declare">
      <Plus className="h-4 w-4" aria-hidden />
      {t('search.action.declare')}
    </Button>
  );
}

/** One PASSENGER search hit — the roster row's visual grammar (PNR primary,
 *  masked lead, status chips) with the whole row as the "work on it" affordance,
 *  plus a slim side action to the CASE itself (or the declare flow when it has
 *  none — "declare" when the hit names a flight). */
function SearchHitRow({
  hit,
  onOpen,
  onOpenCase,
}: {
  hit: DisruptionPnrGroupRow;
  onOpen: () => void;
  onOpenCase: () => void;
}) {
  const { t } = useTranslation('cases');
  const needsHotel = hit.accommodationKind === 'HOTEL' || hit.requiresHotel === true;
  const needsVoucher = hit.accommodationKind === 'MEAL_VOUCHER';
  return (
    <li className="flex items-stretch">
      <button
        type="button"
        onClick={onOpen}
        data-testid="dashboard-search-hit"
        className="flex min-w-0 flex-1 items-center gap-3 p-3 text-left transition-colors hover:bg-muted/40"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{hit.pnrLabel}</span>
            <PnrSourceMark source={hit.pnrSource} className="shrink-0" testId="dashboard-search-hit-source" />
            {hit.leadName ? (
              <span className="text-sm text-muted-foreground">{hit.leadName}</span>
            ) : null}
            <span className="text-xs text-muted-foreground">
              <Users className="mr-0.5 inline h-3 w-3" aria-hidden />
              {hit.paxCount}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <PlaneTakeoff className="h-3 w-3" aria-hidden />
              {hit.flightLabel}
            </span>
            {formatShortDateTime(hit.departureAt) ? (
              <span>{formatShortDateTime(hit.departureAt)}</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {hit.ticketNumber ? (
            <Badge variant="success" size="sm">
              {t('search.badge.ticketed')}
            </Badge>
          ) : (
            <Badge variant={REBOOKING_STATUS_TONE[hit.rebookingStatus] ?? 'secondary'} size="sm">
              {rebookingStatusLabel(hit.rebookingStatus, t)}
            </Badge>
          )}
          {needsHotel ? (
            <Badge variant="secondary" size="sm">
              <Hotel className="mr-0.5 h-3 w-3" aria-hidden /> {t('search.badge.hotel')}
            </Badge>
          ) : null}
          {needsVoucher ? (
            <Badge variant="secondary" size="sm">
              <Utensils className="mr-0.5 h-3 w-3" aria-hidden /> {t('search.badge.voucher')}
            </Badge>
          ) : null}
        </div>
      </button>
      <button
        type="button"
        onClick={onOpenCase}
        data-testid="dashboard-search-hit-case"
        className="flex shrink-0 flex-col items-center justify-center gap-0.5 border-l border-border/60 px-3 text-[11px] text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
      >
        {hit.caseUrn ? (
          <FolderOpen className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <PlaneTakeoff className="h-3.5 w-3.5" aria-hidden />
        )}
        {hit.caseUrn
          ? t('search.action.openCase')
          : hit.flightUrn
            ? t('search.action.declare')
            : t('search.action.createCase')}
      </button>
    </li>
  );
}
